import { useState } from 'react'
import { supabase, readableError } from '../lib/supabase'

/**
 * "Give me everything you hold about me", as a file.
 *
 * export_my_data() has existed and been grantable to `authenticated` since the
 * privacy work went in, and nothing ever called it — an Australian Privacy
 * Principle 12 access right that was implemented and then unreachable. The
 * function does all of it: it self-guards (you get your own data or you are a
 * superadmin), and it logs when staff export somebody else's.
 *
 * The file is built and downloaded in the browser rather than emailed, because
 * the alternative is sending someone's whole record to an address we would
 * then have to be sure of. Nothing leaves the session that made the request.
 */
export default function DataExport({ profile }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [done, setDone] = useState(false)

  const run = async () => {
    setBusy(true); setErr(null)
    const { data, error } = await supabase.rpc('export_my_data')
    setBusy(false)
    if (error) return setErr(readableError(error))

    const stamp = new Date().toISOString().slice(0, 10)
    const url = URL.createObjectURL(
      new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }),
    )
    const a = document.createElement('a')
    a.href = url
    a.download = `beseter-my-data-${stamp}.json`
    a.click()
    // Revoked on the next tick rather than immediately: Safari has not always
    // finished with the blob by the time click() returns.
    setTimeout(() => URL.revokeObjectURL(url), 1000)
    setDone(true)
  }

  return (
    <>
      <button className="action" disabled={busy} aria-busy={busy} onClick={run}>
        <span>
          Download my data
          <small>
            {done
              ? 'Downloaded. Ask again any time.'
              : `Everything held about ${profile.email}, as a file`}
          </small>
        </span>
        <span className="ar">{busy ? '…' : '›'}</span>
      </button>
      {err && <p className="err">{err}</p>}
    </>
  )
}
